import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AGENT_SCOPES } from '@commerce/shared';
import { api, ApiClientError } from '../lib/api.js';
import type { ApiKeyView } from '../lib/types.js';
import { StatusBadge } from '../components/StatusBadge.js';

export function SettingsPage() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['research:write']);
  const [rawKey, setRawKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const keys = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => api.get<ApiKeyView[]>('/api-keys'),
  });

  const create = useMutation({
    mutationFn: () => api.post<ApiKeyView>('/api-keys', { name, scopes }),
    onSuccess: async (res) => {
      setRawKey(res.data.rawKey ?? null);
      setName('');
      await queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
    onError: (err) =>
      setError(err instanceof ApiClientError ? err.message : 'Failed to create key'),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.post(`/api-keys/${id}/revoke`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['api-keys'] }),
  });

  function toggleScope(scope: string) {
    setScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );
  }

  function onCreate(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setRawKey(null);
    create.mutate();
  }

  return (
    <section>
      <h1>Settings</h1>

      <div className="review-panel">
        <h2>Create agent API key</h2>
        <p className="muted">
          Agent keys can only create drafts. They never receive image-generation, approval, or
          credential scopes.
        </p>
        <form className="form" onSubmit={onCreate}>
          <label htmlFor="keyname">Name</label>
          <input id="keyname" value={name} onChange={(e) => setName(e.target.value)} required />
          <fieldset>
            <legend>Scopes</legend>
            {AGENT_SCOPES.map((s) => (
              <label key={s} className="check-inline">
                <input
                  type="checkbox"
                  checked={scopes.includes(s)}
                  onChange={() => toggleScope(s)}
                />
                {s}
              </label>
            ))}
          </fieldset>
          {error && (
            <p role="alert" className="form-error">
              {error}
            </p>
          )}
          <button type="submit" disabled={create.isPending}>
            Create key
          </button>
        </form>
        {rawKey && (
          <div className="policy-note" role="note">
            <strong>Copy this key now — it is shown only once:</strong>
            <br />
            <code>{rawKey}</code>
          </div>
        )}
      </div>

      <h2>Existing keys</h2>
      {keys.data && keys.data.data.length > 0 ? (
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Prefix</th>
              <th>Scopes</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {keys.data.data.map((k) => (
              <tr key={k.id}>
                <td>{k.name}</td>
                <td>
                  <code>{k.keyPrefix}…</code>
                </td>
                <td>{k.scopes.join(', ')}</td>
                <td>
                  <StatusBadge status={k.active ? 'reviewed' : 'rejected'} />
                </td>
                <td>
                  {k.active && (
                    <button
                      type="button"
                      className="button--danger"
                      onClick={() => revoke.mutate(k.id)}
                      disabled={revoke.isPending}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="muted">No API keys yet.</p>
      )}
    </section>
  );
}
